import type { Interactions, ExtendedActions, BindedInteractions } from '../types/Blocks'
import { appNavigateTo } from '@@/shared-components/composables/link'
import { redirect } from '~~/shared-components/composables/redirects'
import { useContent } from '~~/shared-components/content'

export const useInteractions = () => {
  const router = useRouter()

  const searchingBlock = useState('searching-block', () => false)

  const bindInteractions = (
    interactions: Interactions = {},
    isButton = false,
    extendedActions: ExtendedActions = {}
  ) => {
    const callBacks: {
      onClick: (() => void)[]
      onMouseover: (() => void)[]
      onDblclick: (() => void)[]
    } = {
      onClick: [],
      onMouseover: [],
      onDblclick: [],
    }
    const actions: BindedInteractions = {}
    const nuxtApp = useNuxtApp()
    nuxtApp.runWithContext(() => {
      const { formkitEditing } = useContent()
      Object.entries(interactions).forEach(async ([, value]) => {
        switch (value.action) {
          case 'navigateTo': {
            if (value.type) {
              if (!formkitEditing.value) {
                const { slug, identifier: id } = value.object?._link ? value.object._link : {}
                const to = redirect(value.type!, { slug, id }, undefined, undefined, value.object)!
                if (to && value.trigger === 'onClick' && isButton) {
                  actions.to = to
                  if (value.newWindow) {
                    actions.target = value.newWindow ? '_blank' : undefined
                  }
                } else if (to) {
                  if (import.meta.client) {
                    callBacks[value.trigger].push(() => {
                      appNavigateTo(
                        to,
                        value.newWindow
                          ? {
                              open: {
                                target: '_blank',
                              },
                            }
                          : undefined
                      )
                    })
                  }
                }
              } else {
                const { preview } = usePreview()
                const url = await preview({
                  idOrKey: value.object?.id,
                  type: value.type,
                  withSignature: false,
                  redirect: false,
                })
                actions.to = url as string
                actions.target = '_blank'
              }
            }
            break
          }
          case 'goBack': {
            if (import.meta.client) {
              callBacks[value.trigger].push(() => {
                try {
                  router.back()
                  return false
                } catch (e) {
                  console.log(e)
                }
              })
            }
            break
          }
          case 'scrollTo': {
            const to = { hash: `#${value.hash}` }
            if (value.trigger === 'onClick' && isButton) {
              actions.to = to
              callBacks[value.trigger].push(() => {
                const id = to.hash.split('#')[1]
                if (id) scrollToElement(id)
              })
            } else {
              if (import.meta.client) {
                callBacks[value.trigger].push(() => {
                  appNavigateTo(to)
                })
              }
            }
            break
          }
          case 'linkTo': {
            if (value.url) {
              const AutoAddProtocol = (href: string) => {
                try {
                  // Pokud chybí protokol, URL konstruktor v Node.js vyhodí chybu => zkusíme doplnit
                  if (!/^https?:\/\//i.test(href) && href[0] !== '/') {
                    href = `https://${href}`
                  }
                  return new URL(href).href
                } catch (e) {
                  // Pokud URL není validní, vracíme původní (nebo můžeš logovat chybu)
                  return href
                }
              }

              const finalUrl = AutoAddProtocol(value.url)
              const isRelativePath = finalUrl.startsWith('/')

              if (value.trigger === 'onClick' && isButton) {
                actions.to = finalUrl
                actions.external = value.newWindow ? true : isRelativePath
                actions.target = value.newWindow ? '_blank' : undefined
              } else {
                if (import.meta.client) {
                  callBacks[value.trigger].push(() => {
                    appNavigateTo(finalUrl, {
                      external: true,
                      ...(value.newWindow
                        ? {
                            open: {
                              target: '_blank',
                            },
                          }
                        : {}),
                    })
                  })
                }
              }
            }
            break
          }
        }
        Object.entries(extendedActions ?? {}).forEach(([action, data]) => {
          if (value.action === action) {
            Object.entries(data.attrs).forEach(([key, value]) => {
              actions[key] = value
            })
            callBacks[value.trigger].push(() => {
              data.callback(value)
            })
          }
        })
      })
    })
    if (import.meta.client) {
      Object.entries(callBacks).forEach(([key]) => {
        if (!actions[key] && callBacks[key].length) {
          actions[key] = () => {
            callBacks[key]?.forEach((callBack) => {
              callBack()
            })
          }
        }
      })
    }
    return actions
  }
  return { searchingBlock, bindInteractions }
}
